// "Add audio languages to an existing tour" — shared engine core (admin + CLI twin).
//
// The create-mode engine only fills EMPTY tours. This module covers the other
// half of the workflow: a tour that already has its stops (text in every
// language, audio in some) receives the audio for more languages.
//
// Rules (same safety model as the create engine, see TOUR_UPLOAD_AUTOMATION_PLAN.md):
//  - Stops are never created or deleted here. The only write is
//    PUT /api/tourPoints/update/:id with the stop's FULL merged stopLanguagesMap
//    (existing entries byte-for-byte preserved, new languages appended).
//  - A language that already has a non-empty file on the server is never
//    re-uploaded or replaced — "already present" is the resume signal.
//  - Sheet stops are matched to server stops by the create engine's identity
//    (exact English name + latitude within ~55 m). If the name was edited after
//    import, a UNIQUE location match (lat+lng within ~55 m) is accepted with a
//    warning the operator sees on the confirm screen. Anything else is a blocker.
//  - Text (name/description) is never touched — the sheet's non-English tabs
//    are irrelevant to this mode except for deciding which languages are allowed.

import { LIMITS, LANG_ORDER } from "./constants";
import { blocker, warning, info, counts } from "./issues";
import { slugify } from "./validate";

const COORD_TOLERANCE = 0.0005; // degrees, ~55 m — same as the create engine's stopMatcher

/** stopLanguagesMap arrives as a JSON string from the API (longtext column). */
export function parseLangMap(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const v = JSON.parse(raw);
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** Languages that really have audio (an entry with an empty file is a removed upload, not audio). */
export function presentLangs(map) {
  return parseLangMap(map)
    .filter((e) => e && typeof e.language === "string" && typeof e.file === "string" && e.file.length > 0)
    .map((e) => e.language);
}

export function enNameOf(serverStop) {
  try {
    const names = typeof serverStop.name === "string" ? JSON.parse(serverStop.name) : serverStop.name;
    return (names || []).find((n) => n.language === "en")?.text ?? null;
  } catch {
    return null;
  }
}

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Match sheet stops to server stops. Matches CONSUME server rows (id ASC) so one
 * server stop can never satisfy two sheet stops.
 * Pass 1: exact EN name + latitude (the create engine's identity).
 * Pass 2: unique location match for the leftovers (name edited after import) — warning.
 * @returns {{ matches: Map<number, {server: object, by: "name"|"location"}>, issues: object[], unmatchedServer: object[] }}
 */
export function matchStops(sheetStops, serverStops) {
  const issues = [];
  const pool = [...(serverStops || [])].sort((a, b) => a.id - b.id);
  const matches = new Map();

  for (const stop of sheetStops) {
    const idx = pool.findIndex((sp) => enNameOf(sp) === stop.names.en && Math.abs(num(sp.lattitude) - stop.lat) < COORD_TOLERANCE);
    if (idx !== -1) {
      matches.set(stop.sl, { server: pool[idx], by: "name" });
      pool.splice(idx, 1);
    }
  }
  for (const stop of sheetStops) {
    if (matches.has(stop.sl)) continue;
    const candidates = pool.filter(
      (sp) => Math.abs(num(sp.lattitude) - stop.lat) < COORD_TOLERANCE && Math.abs(num(sp.longitude) - stop.lng) < COORD_TOLERANCE
    );
    if (candidates.length === 1) {
      const sp = candidates[0];
      matches.set(stop.sl, { server: sp, by: "location" });
      pool.splice(pool.indexOf(sp), 1);
      issues.push(
        warning(
          "name-differs",
          `stop ${stop.sl}: matched server stop #${sp.id} by location only — sheet name "${stop.names.en}" vs server name "${enNameOf(sp) ?? "?"}" (check it is the same stop; its name is NOT changed)`,
          { sl: stop.sl }
        )
      );
    } else if (candidates.length > 1) {
      issues.push(
        blocker(
          "ambiguous-stop",
          `stop ${stop.sl} "${stop.names.en}": ${candidates.length} server stops at this location and none has this name — rename it in the sheet to match the server`,
          { sl: stop.sl }
        )
      );
    } else {
      issues.push(
        blocker(
          "stop-not-found",
          `stop ${stop.sl} "${stop.names.en}": no server stop with this name or at ${stop.lat}, ${stop.lng} — this mode only adds audio to EXISTING stops`,
          { sl: stop.sl }
        )
      );
    }
  }
  if (pool.length) {
    issues.push(
      warning(
        "server-stop-not-in-sheet",
        `${pool.length} server stop(s) are not in the sheet and will not be touched: ${pool.slice(0, 5).map((sp) => `#${sp.id} ${enNameOf(sp) ?? "?"}`).join(", ")}${pool.length > 5 ? "…" : ""}`
      )
    );
  }
  return { matches, issues, unmatchedServer: pool };
}

/**
 * Compute what the run will do, against the CURRENT server state. Called by the
 * preflight (for the confirm screen) and again at run start / on resume — the
 * server is the source of truth for which languages already exist.
 * @param model   {languages, audioLangs, stops} from validateAddLanguages()
 * @param serverStops  tour.tourPointsDetail from POST /tours/list
 * @returns {{ rows: object[], issues: object[], totals: {stops:number, uploads:number, skipped:number}, langs: string[] }}
 */
export function planAddLanguages(model, serverStops) {
  const audioLangs = LANG_ORDER.filter((l) => (model.audioLangs || []).includes(l));
  const { matches, issues } = matchStops(model.stops, serverStops);
  const rows = [];
  let uploads = 0;
  let skipped = 0;
  for (const stop of model.stops) {
    const m = matches.get(stop.sl);
    if (!m) continue; // blocker already recorded
    const existing = presentLangs(m.server.stopLanguagesMap);
    const add = audioLangs.filter((l) => !existing.includes(l));
    const skip = audioLangs.filter((l) => existing.includes(l));
    uploads += add.length;
    skipped += skip.length;
    rows.push({
      sl: stop.sl,
      stop,
      serverId: m.server.id,
      serverName: enNameOf(m.server) ?? "?",
      matchedBy: m.by,
      existing: LANG_ORDER.filter((l) => existing.includes(l)),
      add,
      skip,
    });
  }
  if (rows.length && uploads === 0) {
    issues.push(info("nothing-to-add", `Every matched stop already has audio in ${audioLangs.join(", ")} — nothing to upload (the finishing steps still run)`));
  } else if (skipped) {
    issues.push(info("already-present", `${skipped} stop/language pair(s) already have audio on the server and will be skipped`));
  }
  const stopsToUpdate = rows.filter((r) => r.add.length).length;
  return { rows, issues, totals: { stops: stopsToUpdate, uploads, skipped }, langs: audioLangs };
}

/**
 * Merge newly uploaded files into a stop's existing map.
 * Existing entries are kept in order and untouched, except an entry for a language
 * being added whose file is empty (a removed upload) — that entry gets the new file.
 * New languages are appended in canonical LANG_ORDER.
 * @param existingMap  array | JSON string from the server
 * @param additions    Object<string,string> — language code -> server filename
 */
export function mergeLanguageMap(existingMap, additions) {
  const out = parseLangMap(existingMap).map((e) => ({ ...e }));
  for (const lang of LANG_ORDER.filter((l) => Object.prototype.hasOwnProperty.call(additions, l))) {
    const file = additions[lang];
    if (typeof file !== "string" || !file.length) continue;
    const existing = out.find((e) => e && e.language === lang);
    if (existing) {
      if (!existing.file) existing.file = file;
      // a non-empty existing file is never replaced (plan excludes these; belt and braces)
    } else {
      out.push({ language: lang, file });
    }
  }
  return out;
}

/** The exact body for PUT /api/tourPoints/update/:id — only the keys we mean to change. */
export function buildUpdatePayload(tourId, mergedMap) {
  // tourId is required: the backend's background transcription keys the
  // transcript by req.body.tourId (undefined would file it under "undefined/").
  return { tourId, stopLanguagesMap: mergedMap };
}

/**
 * Validation for this mode: sheet x audio zips (no images, no create payload).
 * Mirrors the audio checks of validateAll() with identical issue codes.
 * @param opts {audioLangs?: string[]} explicit language selection (CLI --langs)
 */
export function validateAddLanguages(sheet, audioZips, zipIssues = [], opts = {}) {
  const issues = [...sheet.issues, ...zipIssues];
  const languages = sheet.languages;

  const byLang = new Map();
  for (const zip of audioZips) {
    if (!zip.lang) continue; // already a blocker from the zip parser
    if (byLang.has(zip.lang)) issues.push(blocker("dup-lang-zip", `Two audio zips for language "${zip.lang}" (${byLang.get(zip.lang).name} and ${zip.name})`));
    else byLang.set(zip.lang, zip);
    if (!languages.includes(zip.lang)) {
      issues.push(warning("zip-lang-not-in-sheet", `[${zip.name}] language "${zip.lang}" has no tab in the sheet — the stops have no ${zip.lang} text, so this zip is ignored`));
    }
  }

  let audioLangs;
  if (opts.audioLangs && opts.audioLangs.length) {
    audioLangs = LANG_ORDER.filter((l) => opts.audioLangs.includes(l));
    for (const l of opts.audioLangs) {
      if (!languages.includes(l)) issues.push(blocker("audio-lang-not-in-sheet", `--langs includes "${l}" but the sheet has no ${l} tab`));
      else if (!byLang.has(l)) issues.push(blocker("missing-lang-zip", `No audio zip provided for ${l} (selected for this run)`, { lang: l }));
    }
  } else {
    audioLangs = LANG_ORDER.filter((l) => languages.includes(l) && byLang.has(l));
  }
  if (!audioZips.length) issues.push(blocker("no-audio-zips", "Upload at least one language audio zip — this mode adds audio languages"));
  else if (!audioLangs.length) issues.push(blocker("no-audio-langs", "None of the audio zips maps to a language the sheet has a tab for"));
  for (const zip of byLang.values()) {
    if (languages.includes(zip.lang) && !audioLangs.includes(zip.lang)) {
      issues.push(warning("zip-not-selected", `[${zip.name}] provided but ${zip.lang} is not in --langs — it will be ignored this run`));
    }
  }

  for (const stop of sheet.stops) {
    for (const lang of audioLangs) {
      const zip = byLang.get(lang);
      if (!zip) continue;
      const f = zip.files.get(stop.sl);
      if (!f) {
        issues.push(blocker("missing-audio", `stop ${stop.sl}: no ${lang} audio in ${zip.name}`, { sl: stop.sl, lang }));
        continue;
      }
      stop.audio[lang] = f;
      if (f.size === 0) issues.push(blocker("empty-audio", `stop ${stop.sl} ${lang}: audio file is 0 bytes`, { sl: stop.sl, lang }));
      if (f.size > LIMITS.audioBytes) issues.push(blocker("audio-too-big", `stop ${stop.sl} ${lang}: ${(f.size / 1e6).toFixed(1)} MB exceeds the server's 50,000,000-byte upload limit`, { sl: stop.sl, lang }));
      const nameKey = slugify(stop.names.en).replace(/-/g, "");
      const slugKey = (f.slug || "").replace(/-/g, "");
      if (nameKey && slugKey && !(slugKey.includes(nameKey) || nameKey.includes(slugKey))) {
        issues.push(warning("slug-mismatch", `stop ${stop.sl} ${lang}: file "${f.entryName}" slug doesn't resemble "${stop.names.en}" — check the numbering`, { sl: stop.sl, lang }));
      }
    }
  }

  const validSl = new Set(sheet.stops.map((s) => s.sl));
  for (const zip of byLang.values()) {
    for (const sl of zip.files.keys()) {
      if (!validSl.has(sl)) issues.push(warning("orphan-audio", `[${zip.name}] file for stop ${sl} but the sheet has no such stop`, { sl, lang: zip.lang }));
    }
  }

  for (const zip of [...byLang.values()].filter((z) => audioLangs.includes(z.lang))) {
    const mStops = zip.manifest?.stops;
    if (!Array.isArray(mStops)) {
      if (!zip.manifest) issues.push(info("no-manifest", `[${zip.name}] no manifest.json — duration checks skipped for ${zip.lang}`));
      continue;
    }
    for (const ms of mStops) {
      const sl = Number(ms.position);
      if (!validSl.has(sl)) continue;
      const audio = (ms.audios || []).find((a) => a.language === zip.lang) || (ms.audios || [])[0];
      const dur = audio?.durationSeconds;
      if (typeof dur === "number" && dur < LIMITS.minAudioSeconds) {
        issues.push(warning("short-audio", `stop ${sl} ${zip.lang}: only ${Math.round(dur)}s of audio (manifest) — verify the file`, { sl, lang: zip.lang }));
      }
    }
  }

  const c = counts(issues);
  return { model: { languages, audioLangs, stops: sheet.stops }, issues, ok: c.blockers === 0, counts: c };
}
